import { Badge, Box, Button, Text, VStack } from '@chakra-ui/react';
import {
  GoogleMap,
  InfoWindow,
  Marker,
  useJsApiLoader,
} from '@react-google-maps/api';
import React, { useEffect, useState } from 'react';
import DirectionsButton from '../directions-btn/DirectionsButton';
import PracticeStatusBadge from '../practice-status-badge/PracticeStatusBadge';

// Static libraries array to prevent unnecessary reloads
const GOOGLE_MAPS_LIBRARIES: (
  | 'places'
  | 'geometry'
  | 'drawing'
  | 'visualization'
)[] = ['places'];

// Geocoding cache configuration
const GEOCODING_CACHE_EXPIRY_TIME = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
const GEOCODING_CACHE_KEY = 'chs_geocoding_cache';

interface GeocodingCacheEntry {
  lat: number;
  lng: number;
  timestamp: number;
}

interface GeocodingCache {
  [address: string]: GeocodingCacheEntry;
}

/**
 * Get geocoded coordinates from cache
 * Returns null if cache miss or expired
 */
const getCachedGeocode = (address: string): { lat: number; lng: number } | null => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }

    const cachedData = localStorage.getItem(GEOCODING_CACHE_KEY);
    if (!cachedData) {
      return null;
    }

    const cache: GeocodingCache = JSON.parse(cachedData);
    const entry = cache[address];

    if (!entry) {
      return null;
    }

    // Check if cache entry is expired
    if (Date.now() - entry.timestamp > GEOCODING_CACHE_EXPIRY_TIME) {
      // Remove expired entry
      delete cache[address];
      localStorage.setItem(GEOCODING_CACHE_KEY, JSON.stringify(cache));
      return null;
    }

    return { lat: entry.lat, lng: entry.lng };
  } catch (error) {
    console.warn('Failed to read geocoding cache:', error);
    return null;
  }
};

/**
 * Store geocoded coordinates in cache
 */
const setCachedGeocode = (address: string, lat: number, lng: number): void => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    let cache: GeocodingCache = {};
    const cachedData = localStorage.getItem(GEOCODING_CACHE_KEY);

    if (cachedData) {
      try {
        cache = JSON.parse(cachedData);
      } catch (error) {
        console.warn('Failed to parse geocoding cache, creating new cache:', error);
        cache = {};
      }
    }

    // Store new entry
    cache[address] = {
      lat,
      lng,
      timestamp: Date.now(),
    };

    // Clean up expired entries periodically (every 100 entries to avoid performance issues)
    const cacheEntries = Object.keys(cache);
    if (cacheEntries.length > 1000) {
      const now = Date.now();
      const cleanedCache: GeocodingCache = {};
      for (const [key, value] of Object.entries(cache)) {
        if (now - value.timestamp <= GEOCODING_CACHE_EXPIRY_TIME) {
          cleanedCache[key] = value;
        }
      }
      cache = cleanedCache;
    }

    localStorage.setItem(GEOCODING_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    // Handle quota exceeded or other storage errors gracefully
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      console.warn('Geocoding cache storage quota exceeded, clearing old entries');
      try {
        // Clear cache and try again with just this entry
        localStorage.removeItem(GEOCODING_CACHE_KEY);
        const newCache: GeocodingCache = {
          [address]: { lat, lng, timestamp: Date.now() },
        };
        localStorage.setItem(GEOCODING_CACHE_KEY, JSON.stringify(newCache));
      } catch (retryError) {
        console.warn('Failed to save geocoding cache after cleanup:', retryError);
      }
    } else {
      console.warn('Failed to save geocoding cache:', error);
    }
  }
};

interface Location {
  address?: string;
  name?: string;
  id?: string;
  lat?: number | undefined;
  lng?: number | undefined;
  active?: boolean;
  practiceOnPac?: boolean | string | null;
  practiceType?: string;
}

interface MapWithMarkerProps {
  locations?: Location[];
  height?: string | number;
  width?: string | number;
}

const MapWithMarker: React.FC<MapWithMarkerProps> = ({
  height = '100%',
  width = '100%',
  locations = [],
}) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_KEY as string,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
  const [markers, setMarkers] = useState<Location[]>([]);
  const [center, setCenter] =
    useState<google.maps.LatLngLiteral | null>(null);
  const [zoom, setZoom] = useState<number>(4); // Default to USA-wide view
  const [activeMarker, setActiveMarker] = useState<number | null>(
    null
  );

  useEffect(() => {
    const geocodeAddresses = async () => {
      if (!locations.length) {
        console.error('No locations provided');
        return;
      }

      const geocoder = new google.maps.Geocoder();
      const newMarkers: Location[] = [];

      for (const location of locations) {
        if (
          location.lat !== undefined &&
          location.lng !== undefined
        ) {
          newMarkers.push(location); // Use direct lat/lng if available
        } else if (location.address) {
          // Check cache first to avoid unnecessary API calls
          const cachedResult = getCachedGeocode(location.address);

          if (cachedResult) {
            // Use cached coordinates
            newMarkers.push({
              ...location,
              lat: cachedResult.lat,
              lng: cachedResult.lng,
            });
          } else {
            // Cache miss - make API call
            try {
              const results = await new Promise<
                google.maps.GeocoderResult[]
              >((resolve, reject) =>
                geocoder.geocode(
                  { address: location.address },
                  (res, status) => {
                    if (
                      status === google.maps.GeocoderStatus.OK &&
                      res &&
                      res.length > 0
                    ) {
                      resolve(res);
                    } else {
                      reject(
                        `Geocode failed for ${location.address}: ${status}`
                      );
                    }
                  }
                )
              );

              const { lat, lng } = results[0].geometry.location;
              const latValue = lat();
              const lngValue = lng();

              // Store in cache for future use
              setCachedGeocode(location.address, latValue, lngValue);

              newMarkers.push({
                ...location,
                lat: latValue,
                lng: lngValue,
              });
            } catch (error) {
              console.error('Geocoding error:', error);
              // Continue with other locations even if one fails
            }
          }
        }
      }

      setMarkers(newMarkers);

      if (newMarkers.length === 1) {
        setCenter({
          lat: newMarkers[0].lat!,
          lng: newMarkers[0].lng!,
        });
        setZoom(10); // Zoom in closer
      } else if (newMarkers.length > 1) {
        setCenter({ lat: 39.8283, lng: -98.5795 }); // Center over the USA
        setZoom(4);
      } else {
        setCenter({ lat: 39.8283, lng: -98.5795 });
        setZoom(4);
      }
    };

    if (isLoaded) {
      geocodeAddresses();
    }
  }, [isLoaded, locations]);

  const handleActiveMarker = (markerId: number) => {
    setActiveMarker(activeMarker === markerId ? null : markerId);
  };

  const handleOnLoad = (map: google.maps.Map) => {
    if (markers.length > 1) {
      // **Fit all markers into view when there are multiple**
      const bounds = new google.maps.LatLngBounds();
      markers.forEach(
        ({ lat, lng }) => lat && lng && bounds.extend({ lat, lng })
      );
      map.fitBounds(bounds);
    }
  };

  return isLoaded && center ? (
    <Box width={width} height={height}>
      <GoogleMap
        onLoad={handleOnLoad}
        onClick={() => setActiveMarker(null)}
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={center}
        zoom={zoom}
      >
        {markers.map(
          (
            {
              name,
              lat,
              lng,
              id,
              address,
              active,
              practiceOnPac,
              practiceType,
            },
            index
          ) => {
            // Construct Google Maps URL for directions
            const googleMapsUrl = address
              ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  address
                )}`
              : '';

            return (
              <Marker
                key={id || index}
                position={{ lat: lat!, lng: lng! }}
                onClick={() => handleActiveMarker(index)}
                options={{
                  icon: {
                    url:
                      active === true
                        ? 'http://maps.google.com/mapfiles/ms/icons/green-dot.png'
                        : active === false
                        ? 'http://maps.google.com/mapfiles/ms/icons/red-dot.png'
                        : 'http://maps.google.com/mapfiles/ms/icons/orange-dot.png',
                    scaledSize: new google.maps.Size(32, 32),
                  },
                }}
              >
                {activeMarker === index ? (
                  <InfoWindow
                    onCloseClick={() => setActiveMarker(null)}
                  >
                    <Box minW="380px" maxW="480px">
                      <VStack
                        align="start"
                        spacing="0.75rem"
                        p="1rem"
                      >
                        {/* Practice Name with Status Badges */}
                        <VStack
                          align="start"
                          spacing="0.5rem"
                          w="100%"
                        >
                          <Text
                            fontSize="1.125rem"
                            fontWeight={700}
                            lineHeight="1.25rem"
                            color="text.primary"
                            cursor="pointer"
                            wordBreak="break-word"
                            whiteSpace="normal"
                            _hover={{
                              color: 'blue.600',
                              textDecoration: 'underline',
                            }}
                            onClick={() => {
                              if (id) {
                                window.open(
                                  `/practices/${id}`,
                                  '_blank'
                                );
                              }
                            }}
                          >
                            {name}
                          </Text>
                          {/* Status Badges */}
                          <PracticeStatusBadge
                            active={active ?? true}
                            practiceOnPac={practiceOnPac}
                          />

                          {/* Address with Directions Button */}
                          {address && (
                            <VStack
                              align="start"
                              spacing="0.5rem"
                              w="100%"
                            >
                              <Text
                                fontSize="0.875rem"
                                color="text.secondary"
                                lineHeight="1.25rem"
                                wordBreak="break-word"
                                whiteSpace="normal"
                              >
                                {address}
                              </Text>
                              {googleMapsUrl && (
                                <DirectionsButton
                                  googleMapsUrl={googleMapsUrl}
                                />
                              )}
                            </VStack>
                          )}
                        </VStack>

                        {/* Practice Type Badge */}
                        {practiceType && (
                          <Badge
                            colorScheme="blue"
                            variant="outline"
                            fontSize="0.75rem"
                            px="0.5rem"
                            py="0.25rem"
                            borderRadius="0.25rem"
                          >
                            {practiceType}
                          </Badge>
                        )}

                        {/* Action Button */}
                        <Button
                          size="sm"
                          colorScheme="blue"
                          variant="outline"
                          onClick={() => {
                            if (id) {
                              window.open(
                                `/practices/${id}`,
                                '_blank'
                              );
                            }
                          }}
                          fontSize="0.875rem"
                          height="2rem"
                          px="1rem"
                          width="100%"
                        >
                          View Practice Details
                        </Button>
                      </VStack>
                    </Box>
                  </InfoWindow>
                ) : null}
              </Marker>
            );
          }
        )}
      </GoogleMap>
    </Box>
  ) : null;
};

export default MapWithMarker;
